import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Input } from '@1trading/react/components/ui/input';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { PageHeading, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { type InstitutionProducts, type InstitutionProductsOrDefaults, institutionProductsSchema } from '@/features/institution/products/schema';
import type { FormStepGuard } from '@/hooks/use-form-step-guard';

const InstitutionProductsFieldsetLeiNumberLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.products.leiNumber.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.products.leiNumber.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionProductsFieldsetLeiNumberLegend.displayName = 'InstitutionProductsFieldsetLeiNumberLegend';

const InstitutionProductsFieldsetLeiNumber = () => {
  const form = useFormContext<InstitutionProductsOrDefaults>();
  const { t } = useTranslation();

  return (
    <FormField
      control={form.control}
      name="leiNumber"
      render={({ field }) => (
        <FormItem>
          <FormLabel>{t('institutionDetails.products.leiNumber.leiNumber')}*</FormLabel>
          <FormControl>
            <Input {...field} placeholder={t('institutionDetails.company.placeholders.leiNumber')} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
InstitutionProductsFieldsetLeiNumber.displayName = 'InstitutionProductsFieldsetLeiNumber';

// Skipping futures, or enabling it with a valid LEI already on file, leaves this step nothing to ask
const beforeStepEnter: FormStepGuard<InstitutionProducts> = async ({ direction, form, onSubmit }, next) => {
  if (!form) return next(false);

  const products = form.getValues();

  if (direction === 'next' && (await institutionProductsSchema.safeParseAsync(products)).success) {
    await onSubmit(products);

    return next(false);
  }

  return next();
};

InstitutionProductsFieldsetLeiNumber.beforeStepEnter = beforeStepEnter;

export { InstitutionProductsFieldsetLeiNumber, InstitutionProductsFieldsetLeiNumberLegend };
